import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { BODY_CELL_FONT_SIZE } from '../components/common/tableRendering';

// Fixed column widths for a data grid (see tableRendering.js).
//
// Every column is given a width once per set of rows and then held. The browser's automatic
// table layout sized each column to the widest text among the rows the scroll window had
// mounted, so columns jumped every time the window moved. A column is now as wide as the
// widest of its header label, its totals cell (where the grid has one) and its values in a
// sample of the rows, kept between MIN_COLUMN_WIDTH and MAX_COLUMN_WIDTH. Text wider than its
// column (a value outside the sample, or anything past the maximum) is cut off with an
// ellipsis by the grid's cell styles.

const COLUMN_WIDTH_SAMPLE_ROWS = 500;
const MIN_COLUMN_WIDTH = 80;
const MAX_COLUMN_WIDTH = 320;
// A cell's 8px padding on each side and its 1px right border, plus 2px so that text measured
// to the pixel is not cut off by sub-pixel rounding.
const CELL_PADDING_AND_BORDER = 19;
// The separator inside a leaf column's id, e.g. `2024_01§Gross_Premium`.
const DELIMITER = '§';

const labelAsGiven = (header) => header.label;

// Returns a function giving the width in pixels of a string drawn in `font`: the glyphs
// measured on an off-screen canvas, plus the letter spacing the browser adds after every
// character. Each distinct string is measured once, since values such as '-' and '0.00'
// repeat throughout a column.
function createTextMeasurer({ fontFamily, fontWeight, fontSizePx, letterSpacingPx }) {
  const context = document.createElement('canvas').getContext('2d');
  context.font = `${fontWeight} ${fontSizePx}px ${fontFamily}`;
  const widthByText = new Map();
  return (text) => {
    if (!widthByText.has(text)) {
      widthByText.set(text, context.measureText(text).width + text.length * letterSpacingPx);
    }
    return widthByText.get(text);
  };
}

// Returns { [leafId]: width in pixels } for every column in `leafOrder`.
//
//   rows          the grid's rows, in an order that does not change with sorting
//   leafOrder     the leaf column ids, as the grid renders them
//   headerRows    { top, middle, bottom }: the grid's header cells, each { id, label }
//   getCellText   (row, leafId) => the text the cell shows
//   headerText    (header) => the label as drawn, where CSS transforms it; default: as given
//   headerIconWidth  pixels kept beside every header label for an icon, e.g. a sort arrow
//   totalsText    (leafId) => the text of the grid's totals cell, or null for no totals row
//
// The functions must keep their identity between renders (module-level or useCallback):
// the widths are recomputed whenever an input changes, and scrolling must not change any.
export default function useColumnWidths({
  rows,
  leafOrder,
  headerRows,
  getCellText,
  headerText = labelAsGiven,
  headerIconWidth = 0,
  totalsText = null,
}) {
  const theme = useTheme();
  const { top, middle, bottom } = headerRows;

  // The fonts the cells draw in. Every cell takes the theme's body2 type; body cells then
  // set their own size, headers are semi-bold and totals bold.
  const cellFonts = useMemo(() => {
    const { fontFamily, htmlFontSize, body2 } = theme.typography;
    const font = (fontWeight, fontSizeRem) => {
      const fontSizePx = parseFloat(fontSizeRem) * htmlFontSize;
      // body2's letter spacing is in em, a fraction of the font size.
      const letterSpacingPx = parseFloat(body2.letterSpacing) * fontSizePx;
      return { fontFamily, fontWeight, fontSizePx, letterSpacingPx };
    };
    return {
      value: font(400, BODY_CELL_FONT_SIZE),
      header: font(600, body2.fontSize),
      total: font(700, body2.fontSize),
    };
  }, [theme]);

  // The widest value of each column across up to COLUMN_WIDTH_SAMPLE_ROWS rows spread evenly
  // through `rows`, measured as the cell renders it.
  const sampledValueWidths = useMemo(() => {
    const measureValue = createTextMeasurer(cellFonts.value);
    const sampleSize = Math.min(rows.length, COLUMN_WIDTH_SAMPLE_ROWS);
    const sampleRows = [];
    for (let index = 0; index < sampleSize; index += 1) {
      sampleRows.push(rows[Math.floor((index * rows.length) / sampleSize)]);
    }
    const widths = {};
    leafOrder.forEach((leafId) => {
      let widest = 0;
      sampleRows.forEach((row) => {
        widest = Math.max(widest, measureValue(String(getCellText(row, leafId))));
      });
      widths[leafId] = widest;
    });
    return widths;
  }, [rows, leafOrder, getCellText, cellFonts]);

  // Every header cell's label width, with the leaf columns beneath it. A header covers the
  // leaves whose id is its own id, or begins with its id and the delimiter: a period header
  // covers every metric under that period. Listed innermost row first, so that a label
  // spanning several columns is checked against their widths after the labels beneath it
  // have widened them.
  const headerLabelSpans = useMemo(() => {
    const measureHeader = createTextMeasurer(cellFonts.header);
    return [bottom, middle, top].flat().map((header) => {
      const labelWidth = measureHeader(headerText(header)) + CELL_PADDING_AND_BORDER + headerIconWidth;
      return {
        leafIds: leafOrder.filter((leafId) => leafId === header.id || leafId.startsWith(`${header.id}${DELIMITER}`)),
        labelWidth: Math.min(labelWidth, MAX_COLUMN_WIDTH),
      };
    });
  }, [top, middle, bottom, leafOrder, headerText, headerIconWidth, cellFonts]);

  // The final width of every leaf column. Recomputed as a totals row fills in, since a
  // column's sum is usually wider than any one of its values.
  return useMemo(() => {
    const measureTotal = createTextMeasurer(cellFonts.total);
    const widths = {};
    leafOrder.forEach((leafId) => {
      const totalWidth = totalsText ? measureTotal(totalsText(leafId)) : 0;
      const contentWidth = Math.max(sampledValueWidths[leafId], totalWidth) + CELL_PADDING_AND_BORDER;
      widths[leafId] = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, contentWidth));
    });
    // A label wider than the columns beneath it widens them all by an equal share.
    headerLabelSpans.forEach(({ leafIds, labelWidth }) => {
      const spannedWidth = leafIds.reduce((sum, leafId) => sum + widths[leafId], 0);
      if (labelWidth <= spannedWidth) return;
      const extraPerLeaf = (labelWidth - spannedWidth) / leafIds.length;
      leafIds.forEach((leafId) => {
        widths[leafId] += extraPerLeaf;
      });
    });
    return widths;
  }, [leafOrder, sampledValueWidths, headerLabelSpans, totalsText, cellFonts]);
}
